<template>
  <VDialog :icon="props.icon" :title="props.title">
    <VParagraph v-if="props.message" v-a11y:dialog:description v-safe-html="props.message" color="neutral/500" class="text-center" />
    <div v-if="props.fields.length > 0" class="flex flex-col gap-2">
      <div v-for="(field, idx) in props.fields" :key="idx">
        <component
          :is="getFieldComponent(field)"
          ref="field-ref"
          v-model="fields[idx]" 
          v-bind="getFieldProps(field)"
          :focus="idx === 0"
        />
      </div>
    </div>
    <template #buttons>
      <VButton
        v-for="(button, buttonIndex) of buttons"
        :key="buttonIndex"
        :disabled="button.value && !isValid"
        size="sm"
        :variant="button.variant"
        :color="button.color"
        @click="close(button.value)"
      >
        {{ button.label }}
      </VButton>
    </template>
  </VDialog>
</template>
<script lang="ts" setup>
import VDialog from './VDialog.vue'
import VButton from '@component-library/buttons/VButton.vue';
import VInput from '@component-library/inputs/VInput.vue';
import VNumber from '@component-library/inputs/VNumber.vue';
import VPassword from '@component-library/inputs/VPassword.vue';
import VEmail from '@component-library/inputs/VEmail.vue';
import type { Icon } from '@icons/index';
import { computed, ref } from 'vue';
import type { V } from '@component-utils/types';
import VParagraph from '@component-library/labels/VParagraph.vue';
import { useLocalize } from '@component-utils/localization';
import type { DialogField, DialogFieldComponentType, DialogFieldReturnType } from '@component-utils/dialogs';
import { useComponentValidation } from '@component-library/inputs/helpers/validations';
import { useTemplateRef } from '@component-utils/reactivity';
import VTextArea from '@component-library/inputs/VTextArea.vue';
import VSelect from '@component-library/inputs/VSelect.vue';
import VCheckbox from '@component-library/inputs/VCheckbox.vue';

const localize = useLocalize('component-library.dialogs')

defineOptions({
  name: 'VSimpleDialog'
})

const props = withDefaults(
  defineProps<{
    message?: string
    icon?: Icon
    title?: string
    buttons?: (keyof typeof PREDEFINED_BUTTONS | V.SimpleDialog.Button)[]
    fields?: DialogField[]
  }>(),
  {
    icon: undefined,
    title: undefined,
    message: undefined,
    buttons: () => ['close'],
    fields: () => []
  }
)

const isValid = useComponentValidation(
  useTemplateRef('field-ref')
)

const fields = ref<Array<DialogFieldReturnType<DialogField> | undefined | null>>(props.fields.map((field) => field.default))

const emit = defineEmits<{
  close: [V.SimpleDialog.Button['value'], ...Array<DialogFieldReturnType<DialogField> | undefined | null>]
}>()

const getFieldComponent = (field: DialogField): DialogFieldComponentType<typeof field> => {
  return {
    'text': VInput,
    'number': VNumber,
    'password': VPassword,
    'email': VEmail,
    'textarea': VTextArea,
    'select': VSelect,
    'checkbox': VCheckbox
  }[field.type]
}

const getFieldProps = (field: DialogField) => {
  const { type, default: _default, ...props } = field

  return props
}

const close = (value: V.SimpleDialog.Button['value']) => {
  emit('close', value, ...fields.value)
}

const buttons = computed(() => props.buttons.map((button) => typeof button === 'string' ? PREDEFINED_BUTTONS[button] : button))

const PREDEFINED_BUTTONS = {
  'close': {
    value: false,
    label: localize('default_close_label'),
    variant: 'outlined',
    color: 'secondary'
  },
  'continue': {
    value: true,
    label: localize('default_continue_label'),
    variant: 'contained',
    color: 'primary'
  },
  'save': {
    value: true,
    label: localize('default_save_label'),
    variant: 'contained',
    color: 'primary'
  },
  'no': {
    value: false,
    label: localize('default_no_label'),
    variant: 'outlined',
    color: 'secondary'
  },
  'ok': {
    value: true,
    label: localize('default_ok_label'),
    variant: 'contained',
    color: 'primary'
  },
  'confirm': {
    value: true,
    label: localize('default_confirm_label'),
    variant: 'contained',
    color: 'primary'
  },
  'cancel': {
    value: false,
    label: localize('default_cancel_label'),
    variant: 'outlined',
    color: 'secondary'
  }
} as const
</script>